import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import SummarizeIcon from '@mui/icons-material/Summarize';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import dayjs from 'dayjs';

import paths from '@src/paths';
import { useMessage } from '@src/context/common';
import apiClient from '@src/api/clients';
import {
  bulkGenerateInvoices,
  downloadInvoicePdf,
  listInvoices,
  sendInvoice,
} from '@src/api/invoices';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'outstanding', label: 'Outstanding' },
  { value: 'paid', label: 'Paid' },
];

const DEFAULT_PAGE_SIZE = 25;

const getDefaultPeriod = () => {
  const end = dayjs().subtract(1, 'month').endOf('month');
  const start = end.startOf('month');
  return {
    start: start.format('YYYY-MM-DD'),
    end: end.format('YYYY-MM-DD'),
  };
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  const amount = Number(value);
  if (Number.isNaN(amount)) {
    return String(value);
  }
  return amount.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
};

const statusColor = {
  draft: 'default',
  sent: 'info',
  outstanding: 'warning',
  paid: 'success',
};

const resolveStatusColor = (status) => statusColor[status] || 'default';

function mapInvoicesResponse(data) {
  if (Array.isArray(data)) {
    return { results: data, count: data.length };
  }
  if (data && Array.isArray(data.results)) {
    return { results: data.results, count: data.count ?? data.results.length };
  }
  return { results: [], count: 0 };
}

export default function InvoiceListPage() {
  const message = useMessage();
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    issueStart: '',
    issueEnd: '',
  });
  const [pagination, setPagination] = useState({ page: 0, rowsPerPage: DEFAULT_PAGE_SIZE });
  const [invoices, setInvoices] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [error, setError] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);

  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);

  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [bulkPeriod, setBulkPeriod] = useState(() => getDefaultPeriod());
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkSummary, setBulkSummary] = useState(null);
  const [bulkError, setBulkError] = useState('');

  const loadClients = useCallback(() => {
    setClientsLoading(true);
    apiClient
      .request({ url: 'dashboard/clients/', method: 'GET' })
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
        setClients(
          data
            .map((client) => ({ id: client.id, name: client.name }))
            .sort((a, b) => a.name.localeCompare(b.name))
        );
      })
      .catch((err) => {
        console.error('Failed to load clients', err);
        message.error('Unable to load clients for bulk generation.');
      })
      .finally(() => setClientsLoading(false));
  }, [message]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const fetchInvoices = useCallback(() => {
    setLoading(true);
    setError('');
    const params = {
      page: pagination.page + 1,
      page_size: pagination.rowsPerPage,
      search: filters.search,
      status: filters.status !== 'all' ? filters.status : undefined,
      issue_date__gte: filters.issueStart || undefined,
      issue_date__lte: filters.issueEnd || undefined,
      ordering: '-issue_date',
    };
    listInvoices(params)
      .then((res) => {
        const mapped = mapInvoicesResponse(res.data);
        setInvoices(mapped.results);
        setTotalCount(mapped.count);
      })
      .catch((err) => {
        console.error('Failed to fetch invoices', err);
        setError('Failed to fetch invoices. Please try again.');
      })
      .finally(() => setLoading(false));
  }, [filters, pagination.page, pagination.rowsPerPage]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handleFilterChange = (key) => (event) => {
    const value = event.target.value;
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (event, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (event) => {
    setPagination({ page: 0, rowsPerPage: parseInt(event.target.value, 10) });
  };

  const handleQuickSend = (invoiceId) => {
    setSendingId(invoiceId);
    sendInvoice(invoiceId, {})
      .then((res) => {
        message.success('Invoice sent successfully. Stripe payment link created.');
        setInvoices((prev) => prev.map((invoice) => (invoice.id === invoiceId ? res.data : invoice)));
      })
      .catch((err) => {
        console.error('Failed to send invoice', err);
        message.error('Failed to send the invoice. Please review invoice details.');
      })
      .finally(() => setSendingId(null));
  };

  const handleDownloadPdf = useCallback(
    (invoice) => {
      if (!invoice?.id) {
        return;
      }
      setDownloadingId(invoice.id);
      downloadInvoicePdf(invoice.id)
        .then((blob) => {
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          const filename = `${invoice.number || `invoice-${invoice.id}`}.pdf`;
          link.href = blobUrl;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(blobUrl);
        })
        .catch((err) => {
          console.error('Failed to download invoice PDF', err);
          message.error('Unable to download invoice PDF.');
        })
        .finally(() => setDownloadingId(null));
    },
    [message]
  );

  const openBulkDialog = () => {
    setBulkSummary(null);
    setBulkError('');
    setSelectedClientIds([]);
    setBulkPeriod(getDefaultPeriod());
    setBulkDialogOpen(true);
  };

  const closeBulkDialog = () => {
    setBulkDialogOpen(false);
  };

  const handleBulkSubmit = () => {
    if (!selectedClientIds.length) {
      setBulkError('Please choose at least one client.');
      return;
    }
    setBulkSubmitting(true);
    setBulkError('');
    setBulkSummary(null);
    const payload = {
      client_ids: selectedClientIds,
      period_start: bulkPeriod.start || undefined,
      period_end: bulkPeriod.end || undefined,
    };
    bulkGenerateInvoices(payload)
      .then((res) => {
        setBulkSummary(res.data);
        message.success('Invoices generated successfully.');
        fetchInvoices();
      })
      .catch((err) => {
        console.error('Failed to bulk generate invoices', err);
        setBulkError('Unable to generate invoices. Please review your selections.');
      })
      .finally(() => setBulkSubmitting(false));
  };

  const bulkSummaryDetails = useMemo(() => {
    if (!bulkSummary) {
      return null;
    }
    const created = bulkSummary.created || [];
    const skipped = bulkSummary.skipped_client_ids || [];
    return (
      <Stack spacing={1} mt={2}>
        <Alert severity="success">
          {created.length ? `${created.length} invoice(s) created.` : 'No new invoices were created.'}
        </Alert>
        {!!created.length && (
          <Paper variant="outlined">
            <Box p={2}>
              <Typography variant="subtitle2" gutterBottom>
                Created Invoices
              </Typography>
              <Stack spacing={0.5}>
                {created.map((invoice) => (
                  <Stack key={invoice.id} direction="row" spacing={1} alignItems="center">
                    <SummarizeIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {invoice.number || `Invoice #${invoice.id}`} — {invoice.client_name} ({invoice.period_start} → {invoice.period_end})
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          </Paper>
        )}
        {!!skipped.length && (
          <Alert severity="warning">
            Skipped client IDs: {skipped.join(', ')}
          </Alert>
        )}
      </Stack>
    );
  }, [bulkSummary]);

  return (
    <Box p={3}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} mb={3}>
        <Typography variant="h4">Invoices</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} width={{ xs: '100%', sm: 'auto' }}>
          <Button
            variant="outlined"
            startIcon={<SummarizeIcon />}
            onClick={openBulkDialog}
          >
            Bulk generate
          </Button>
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            color="primary"
            onClick={() => navigate(paths.dashboard.invoices.create)}
          >
            New invoice
          </Button>
        </Stack>
      </Stack>

      <Card>
        <CardHeader
          title="Filters"
          action={
            <IconButton onClick={fetchInvoices} aria-label="Refresh">
              <RefreshIcon />
            </IconButton>
          }
        />
        <Divider />
        <CardContent>
          <Stack spacing={2} direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }}>
            <TextField
              label="Search"
              placeholder="Search by invoice number or client"
              value={filters.search}
              onChange={handleFilterChange('search')}
              fullWidth
            />
            <FormControl sx={{ minWidth: 160 }}>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={filters.status}
                onChange={handleFilterChange('status')}
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              type="date"
              label="Issued after"
              value={filters.issueStart}
              onChange={handleFilterChange('issueStart')}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              type="date"
              label="Issued before"
              value={filters.issueEnd}
              onChange={handleFilterChange('issueEnd')}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </CardContent>
      </Card>

      <Box mt={3}>
        <Card>
          <CardHeader title="Invoice list" />
          <Divider />
          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {error && (
                <Alert severity="error" sx={{ mx: 3, mt: 3 }}>
                  {error}
                </Alert>
              )}
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Invoice</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell>Period</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Total</TableCell>
                      <TableCell>Issue date</TableCell>
                      <TableCell>Due date</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoices.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                          <Typography color="text.secondary">No invoices found for the selected filters.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      invoices.map((invoice) => (
                        <TableRow key={invoice.id} hover>
                          <TableCell>
                            <Stack spacing={0.5}>
                              <Typography variant="body2" fontWeight={600}>
                                {invoice.number || `Invoice #${invoice.id}`}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Needs amount review: {invoice.needs_amount_confirmation ? 'Yes' : 'No'}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{invoice.client_name || invoice.client}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {invoice.period_start} – {invoice.period_end}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={invoice.status?.toUpperCase() || 'UNKNOWN'}
                              color={resolveStatusColor(invoice.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{formatCurrency(invoice.total_amount)}</TableCell>
                          <TableCell>{invoice.issue_date || '—'}</TableCell>
                          <TableCell>{invoice.due_date || '—'}</TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Tooltip title="View details">
                                <IconButton
                                  component={RouterLink}
                                  to={paths.dashboard.invoices.details(invoice.id)}
                                  color="primary"
                                  size="small"
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip
                                title={
                                  invoice.status === 'paid'
                                    ? 'Paid invoices cannot be edited'
                                    : 'Edit invoice'
                                }
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    disabled={invoice.status === 'paid'}
                                    {...(invoice.status !== 'paid'
                                      ? {
                                          component: RouterLink,
                                          to: paths.dashboard.invoices.edit(invoice.id),
                                        }
                                      : {})}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Download PDF">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="inherit"
                                    onClick={() => handleDownloadPdf(invoice)}
                                    disabled={downloadingId === invoice.id}
                                  >
                                    {downloadingId === invoice.id ? (
                                      <CircularProgress size={18} />
                                    ) : (
                                      <DescriptionIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Send invoice">
                                <span>
                                  <IconButton
                                    color="secondary"
                                    size="small"
                                    disabled={invoice.status === 'paid' || sendingId === invoice.id}
                                    onClick={() => handleQuickSend(invoice.id)}
                                  >
                                    {sendingId === invoice.id ? <CircularProgress size={18} /> : <SendIcon fontSize="small" />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={totalCount}
                page={pagination.page}
                onPageChange={handlePageChange}
                rowsPerPage={pagination.rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 25, 50]}
              />
            </>
          )}
        </Card>
      </Box>

      <Dialog open={bulkDialogOpen} onClose={closeBulkDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk generate invoices</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <TextField
              select
              label="Clients"
              value={selectedClientIds}
              onChange={(event) => setSelectedClientIds(event.target.value)}
              SelectProps={{ multiple: true, renderValue: (selected) => `${selected.length} selected` }}
              helperText="Choose one or more clients to generate invoices for the selected period."
            >
              {clientsLoading ? (
                <MenuItem disabled>Loading clients…</MenuItem>
              ) : (
                clients.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip size="small" label={client.id} />
                      <Typography variant="body2">{client.name}</Typography>
                    </Stack>
                  </MenuItem>
                ))
              )}
            </TextField>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                type="date"
                label="Period start"
                value={bulkPeriod.start}
                onChange={(event) => setBulkPeriod((prev) => ({ ...prev, start: event.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                type="date"
                label="Period end"
                value={bulkPeriod.end}
                onChange={(event) => setBulkPeriod((prev) => ({ ...prev, end: event.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Stack>
            <Alert severity="info">
              Leave the period empty to use the default billing window for this cycle.
            </Alert>
            {bulkError && <Alert severity="error">{bulkError}</Alert>}
            {bulkSummaryDetails}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeBulkDialog}>Cancel</Button>
          <LoadingButton
            variant="contained"
            loading={bulkSubmitting}
            onClick={handleBulkSubmit}
            startIcon={<SummarizeIcon />}
          >
            Generate
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
